<template>
    <div class="note-list">

        <div class="note-list-header">
            <div class="note-list-search">
                <input type="text" v-model="searchQuery" :placeholder="$t('pages.module.NOTE.list.search')" class="note-search-input" @input="onSearchInput"/>
            </div>
            <button class="note-list-add-btn" @click="$emit('newNote')" :title="$t('pages.module.NOTE.list.newNote')">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
        </div>


        <div class="note-list-items" v-if="groupedNotes.length > 0" :style="swipeX !== 0 ? {overflow: 'hidden'} : null">
            <template v-for="group in groupedNotes" :key="group.label">
                <div class="note-group-header">{{ group.label }}</div>
                <div v-for="note in group.notes" :key="note.id" class="note-swipe-container">
                    <div class="note-swipe-action" :class="note.is_pinned ? 'note-swipe-action--unpin' : 'note-swipe-action--pin'" v-show="swipingNoteId === note.id && swipeX > 0">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" stroke="#fff" stroke-width="2"><path d="M9 4v6l-2 4h5v6l1-1 1 1v-6h5l-2-4V4"/><line x1="8" y1="4" x2="16" y2="4"/></svg>
                    </div>

                    <div class="note-swipe-action note-swipe-action--delete" v-show="swipingNoteId === note.id && swipeX < 0">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </div>

                    <div class="note-item"
                         :class="{active: selectedNoteId === note.id && selectedNoteIds.length <= 1, 'multi-selected': selectedSet.has(note.id), 'touch-drag-ready': touchDragReadyId === note.id}"
                         :style="swipingNoteId === note.id ? {transform: 'translateX(' + swipeX + 'px)', transition: 'none', borderRadius: 0} : null"
                         :draggable="!compact && note.is_owner !== false"
                         @click="onNoteClick(note, $event)"
                         @contextmenu.prevent="$emit('noteContextMenu', $event, note)"
                         @dragstart="onDragStart($event, note)"
                         @dragend="onDragEnd"
                         @touchstart.passive="onLongPressStart($event, note)"
                         @touchmove.passive="onLongPressMove($event)"
                         @touchend.passive="onLongPressEnd"
                         @pointerdown="onSwipeStart($event, note)"
                         @pointermove="onSwipeMove($event)"
                         @pointerup="onSwipeEnd($event, note)"
                         @pointercancel="onSwipeCancel">

                        <div class="note-item-color-bar" v-if="note.color" :style="{background: note.color}"></div>

                        <div class="note-item-content">
                            <div class="note-item-top">
                                <span class="note-item-title">{{ note.title || $t('pages.module.NOTE.list.newNote') }}</span>
                                <span class="note-item-date">{{ formatSmartDate(note.updated_at || note.created_at) }}</span>
                            </div>
                            <div class="note-item-bottom">
                                <span class="note-item-snippet" :class="{'note-item-snippet--empty': !note.preview}">{{ note.preview || $t('pages.module.NOTE.list.noPreview') }}</span>
                            </div>
                            <div class="note-item-meta">
                                <span v-if="note._isTemp" class="note-item-draft">
                                    {{ $t('pages.module.NOTE.list.draft') }}
                                </span>
                                <span v-if="note.is_owner === false && note.user" class="note-item-shared">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                    {{ note.user.full_name }}
                                </span>
                                <span v-if="note.category && note.is_owner !== false" class="note-item-category" :style="note.category.color ? {background: note.category.color + '20', color: note.category.color} : {}">
                                    {{ note.category.name }}
                                </span>
                                <span v-if="note.visibility && note.visibility.id === 2" class="note-item-visibility">
                                    👥 {{ $t('pages.module.NOTE.list.visibilityGroup.' + $handler.platformType) }}
                                </span>
                                <span v-else-if="note.visibility && note.visibility.id === 3" class="note-item-visibility">
                                    🔒 {{ $t('pages.module.NOTE.list.visibilitySelected') }}
                                </span>
                                <span v-if="note.has_agenda" class="note-item-agenda">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>


        <div class="note-list-empty" v-else>
            <div class="note-list-empty-icon">{{ activeView === 'shared' ? '👥' : '📝' }}</div>
            <div class="note-list-empty-title">{{ activeView === 'shared' ? $t('pages.module.NOTE.list.sharedEmpty') : $t('pages.module.NOTE.list.empty') }}</div>
            <div class="note-list-empty-desc">{{ activeView === 'shared' ? $t('pages.module.NOTE.list.sharedEmptyDescription') : $t('pages.module.NOTE.list.emptyDescription') }}</div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NoteList',
    props: {
        notes: {type: Array, default: () => []},
        selectedNoteId: {type: Number, default: null},
        selectedNoteIds: {type: Array, default: () => []},
        activeView: {type: String, default: 'all'},
        compact: {type: Boolean, default: false},
    },
    emits: ['selectNote', 'newNote', 'search', 'noteContextMenu', 'multiSelectNote', 'swipePin', 'swipeDelete', 'touchDragCategory', 'touchDragTrash'],
    data() {
        return {
            searchQuery: '',
            searchTimer: null,
            swipingNoteId: null,
            swipeX: 0,
            touchDragReadyId: null,
        }
    },
    computed: {
        groupedNotes() {
            const groups = []
            const pinned = []
            const todayNotes = []
            const yesterdayNotes = []
            const last7Notes = []
            const last30Notes = []
            const olderMap = {}

            const now = this.$moment()
            const todayStr = now.format('YYYY-MM-DD')
            const yesterdayStr = now.clone().subtract(1, 'day').format('YYYY-MM-DD')

            for (const note of this.notes) {
                if (note.is_pinned) {
                    pinned.push(note)
                    continue
                }

                const noteDate = this.$moment(note.updated_at || note.created_at)
                const noteDateStr = noteDate.format('YYYY-MM-DD')
                const diffDays = now.diff(noteDate, 'days')

                if (noteDateStr === todayStr) {
                    todayNotes.push(note)
                } else if (noteDateStr === yesterdayStr) {
                    yesterdayNotes.push(note)
                } else if (diffDays < 7) {
                    last7Notes.push(note)
                } else if (diffDays < 30) {
                    last30Notes.push(note)
                } else {
                    const monthKey = noteDate.format('MMMM YYYY')
                    if (!olderMap[monthKey]) olderMap[monthKey] = []
                    olderMap[monthKey].push(note)
                }
            }

            if (pinned.length) groups.push({label: this.$t('pages.module.NOTE.list.pinned'), notes: pinned})
            if (todayNotes.length) groups.push({label: this.$t('pages.module.NOTE.list.today'), notes: todayNotes})
            if (yesterdayNotes.length) groups.push({label: this.$t('pages.module.NOTE.list.yesterday'), notes: yesterdayNotes})
            if (last7Notes.length) groups.push({label: this.$t('pages.module.NOTE.list.last7Days'), notes: last7Notes})
            if (last30Notes.length) groups.push({label: this.$t('pages.module.NOTE.list.last30Days'), notes: last30Notes})

            for (const [monthKey, notes] of Object.entries(olderMap)) {
                groups.push({label: monthKey, notes})
            }

            return groups
        },
        selectedSet() {
            return new Set(this.selectedNoteIds)
        },
    },
    methods: {
        formatSmartDate(dateStr) {
            if (!dateStr) return ''
            const date = this.$moment(dateStr)
            const now = this.$moment()
            const todayStr = now.format('YYYY-MM-DD')
            const yesterdayStr = now.clone().subtract(1, 'day').format('YYYY-MM-DD')
            const dateFormatted = date.format('YYYY-MM-DD')
            const diffDays = now.diff(date, 'days')

            if (dateFormatted === todayStr) return date.format('HH:mm')
            if (dateFormatted === yesterdayStr) return this.$t('pages.module.NOTE.list.yesterday')
            if (diffDays < 7) return date.format('dddd')
            return date.format('D.MM.YYYY')
        },
        onSearchInput() {
            clearTimeout(this.searchTimer)
            this.searchTimer = setTimeout(() => {
                this.$emit('search', this.searchQuery)
            }, 500)
        },
        onNoteClick(note, event) {
            if (this._longPressed) {
                this._longPressed = false
                return
            }
            if (this._swiped) {
                this._swiped = false
                return
            }
            const isCtrlOrCmd = event.metaKey || event.ctrlKey
            const isShift = event.shiftKey
            if (isCtrlOrCmd || isShift) {
                this.$emit('multiSelectNote', {noteId: note.id, isCtrlOrCmd, isShift, note})
            } else {
                this.$emit('selectNote', note.id)
            }
        },
        onLongPressStart(event, note) {
            const touch = event.touches[0]
            this._lpStartX = touch.clientX
            this._lpStartY = touch.clientY
            this._longPressed = false
            this._longPressReady = false
            this._lpNote = note
            this._longPressTimer = setTimeout(() => {
                this._longPressReady = true
                this._longPressTimer = null
                this._vibrate(15)
            }, 350)
        },
        onLongPressEnd(event) {
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null

            if (this._longPressReady) {
                const touch = event.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._lpStartX)
                const dy = Math.abs(touch.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this._longPressed = true
                    this.$emit('noteContextMenu', {clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {}}, this._lpNote)
                    return
                }
            }
            this._longPressReady = false
        },
        onLongPressMove(e) {
            if (!this._longPressTimer && !this._longPressReady) return
            const touch = e.touches[0]
            const dx = Math.abs(touch.clientX - this._lpStartX)
            const dy = Math.abs(touch.clientY - this._lpStartY)
            if (dx > 20 || dy > 20) {
                clearTimeout(this._longPressTimer)
                this._longPressTimer = null
                this._longPressReady = false
            }
        },

        onSwipeStart(e, note) {
            if (e.button && e.button !== 0) return
            if (note.is_owner === false) return
            if (this.selectedNoteIds.length > 1) return
            this._swipeStartX = e.clientX
            this._swipeStartY = e.clientY
            this._swipeLocked = false
            this._swiped = false
            this._swipingNote = note
            this._isSwipeGesture = false
            this._swipePointerType = e.pointerType
            this._swipeOverThreshold = false

            clearTimeout(this._touchDragTimer)
            this._touchDragActive = false
            this._touchDragPending = false

            if (e.pointerType !== 'mouse' && !this.compact) {
                this._swipeCardEl = e.target.closest('.note-item')
                document.addEventListener('touchmove', this._onTouchDragMove, {passive: false})
                document.addEventListener('touchend', this._onTouchDragEnd)
                document.addEventListener('touchcancel', this._onTouchDragEnd)

                this._touchDragTimer = setTimeout(() => {
                    this._touchDragStartPos = {x: this._swipeStartX, y: this._swipeStartY}
                    this._swipeStartX = null
                    this._swipeLocked = false
                    this.swipingNoteId = null
                    this.swipeX = 0
                    clearTimeout(this._longPressTimer)
                    this._longPressReady = false
                    this._touchDragPending = true
                    this._touchDragNote = note
                    this.touchDragReadyId = note.id
                    this._vibrate(15)
                }, 300)
            }
        },
        onSwipeMove(e) {
            if (this._touchDragPending) {
                this._startTouchDrag(this._touchDragNote, e.clientX, e.clientY)
                return
            }
            if (this._touchDragActive) return
            if (this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const dy = e.clientY - this._swipeStartY

            if (!this._swipeLocked) {
                if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return
                if (this._swipePointerType === 'mouse' ? Math.abs(dy) > Math.abs(dx) * 1.5 : Math.abs(dy) >= Math.abs(dx)) {
                    return
                }
                this._swipeLocked = true
                this._isSwipeGesture = true
                this._swiped = true
                this.swipingNoteId = this._swipingNote.id
                clearTimeout(this._touchDragTimer)
                clearTimeout(this._longPressTimer)
                this._longPressReady = false
                if (this._swipePointerType === 'mouse') {
                    document.addEventListener('mousemove', this._onDocMouseMove)
                    document.addEventListener('mouseup', this._onDocMouseUp)
                } else {
                    document.addEventListener('pointermove', this.onDocSwipeMove)
                    document.addEventListener('pointerup', this.onDocSwipeUp)
                }
            }

            if (this._swipePointerType !== 'mouse') e.preventDefault()
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        _onDocMouseMove(e) {
            if (!this._swipeLocked || this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        _onDocMouseUp() {
            document.removeEventListener('mousemove', this._onDocMouseMove)
            document.removeEventListener('mouseup', this._onDocMouseUp)
            if (this._swipeLocked && this._swipingNote) {
                this._resolveSwipe(this._swipingNote)
            }
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false

        },
        onDocSwipeMove(e) {
            if (!this._swipeLocked || this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        onDocSwipeUp() {
            document.removeEventListener('pointermove', this.onDocSwipeMove)
            document.removeEventListener('pointerup', this.onDocSwipeUp)
            if (this._swipeLocked && this._swipingNote) {
                this._resolveSwipe(this._swipingNote)
            }
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false

        },
        onSwipeEnd(e, note) {
            if (this._touchDragActive || this._touchDragPending) return
            if (this._swipeStartX == null && !this._swipeLocked) {

                return
            }
            if (this._swipePointerType === 'mouse' && this._swipeLocked) return
            if (!this._swipeLocked) {
                this._swipeStartX = null
                this.swipingNoteId = null
    
                return
            }
            document.removeEventListener('pointermove', this.onDocSwipeMove)
            document.removeEventListener('pointerup', this.onDocSwipeUp)
            this._resolveSwipe(note)
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false

        },
        _resolveSwipe(note) {
            const dx = this.swipeX
            const threshold = 60
            if (dx > threshold) {
                this.swipeX = 0
                this.swipingNoteId = null
                this.$emit('swipePin', note)
            } else if (dx < -threshold) {
                this.swipeX = 0
                this.swipingNoteId = null
                this.$emit('swipeDelete', note)
            } else {
                this.swipeX = 0
                this.swipingNoteId = null
            }
        },
        onSwipeCancel() {
            if (this._touchDragActive || this._touchDragPending) return
            if (this._touchDragTimer) {
                clearTimeout(this._touchDragTimer)
                this._touchDragTimer = null
                this._cleanupTouchDragListeners()
            }
            if (this._isSwipeGesture) return
            this.swipeX = 0
        },

        _vibrate(ms) {
            if (navigator.vibrate) navigator.vibrate(ms)
        },
        _checkSwipeThreshold() {
            const isOver = Math.abs(this.swipeX) >= 60
            if (isOver !== this._swipeOverThreshold) {
                this._vibrate(10)
                this._swipeOverThreshold = isOver
            }
        },

        _onTouchDragMove(e) {
            if (this._touchDragPending) {
                const touch = e.touches[0]
                this._startTouchDrag(this._touchDragNote, touch.clientX, touch.clientY)
                e.preventDefault()
                return
            }
            if (this._touchDragActive) {
                e.preventDefault()
                const touch = e.touches[0]
                if (this._dragGhost) {
                    const w = this._dragGhostW || this._dragGhost.offsetWidth
                    this._dragGhost.style.transform = `translate(${touch.clientX - w / 2}px, ${touch.clientY - 20}px)`
                }
                const el = document.elementFromPoint(touch.clientX, touch.clientY)
                const catItem = el?.closest('[data-category-id]')
                const trashItem = el?.closest('.sidebar-trash-item')
                const target = catItem || trashItem

                if (target !== this._dragHighlightEl) {
                    if (this._dragHighlightEl) {
                        this._dragHighlightEl.classList.remove('drag-over')
                        this._dragHighlightEl.classList.remove('drag-over-trash')
                    }
                    if (catItem) catItem.classList.add('drag-over')
                    else if (trashItem) trashItem.classList.add('drag-over-trash')
                    this._dragHighlightEl = target || null
                }
                return
            }
            if (this._touchDragTimer && this._swipeStartY != null) {
                const touch = e.touches[0]
                const dy = Math.abs(touch.clientY - this._swipeStartY)
                if (dy > 10) {
                    clearTimeout(this._touchDragTimer)
                    this._touchDragTimer = null
                    this._cleanupTouchDragListeners()
                }
            }
        },
        _onTouchDragEnd(e) {
            clearTimeout(this._touchDragTimer)
            this._touchDragTimer = null

            if (this._touchDragActive) {
                const touch = e.changedTouches[0]
                const el = document.elementFromPoint(touch.clientX, touch.clientY)
                const catItem = el?.closest('[data-category-id]')
                const trashItem = el?.closest('.sidebar-trash-item')

                if (catItem && this._touchDragNote) {
                    const categoryId = parseInt(catItem.dataset.categoryId, 10)
                    if (!isNaN(categoryId)) {
                        this.$emit('touchDragCategory', {noteId: this._touchDragNote.id, categoryId})
                    }
                } else if (trashItem && this._touchDragNote) {
                    this.$emit('touchDragTrash', {noteId: this._touchDragNote.id})
                }
                this._cleanupTouchDrag()
                return
            }

            if (this._touchDragPending) {
                this._swiped = true
                this._longPressed = true
                const note = this._touchDragNote
                const pos = this._touchDragStartPos
                if (note && pos) {
                    this.$emit('noteContextMenu', {
                        clientX: pos.x,
                        clientY: pos.y,
                        preventDefault() {}
                    }, note)
                }
                this._cleanupTouchDrag()
                return
            }

            this._cleanupTouchDragListeners()
        },
        _startTouchDrag(note, x, y) {
            this._swiped = true
            this._touchDragActive = true
            this._touchDragPending = false
            this.touchDragReadyId = null

            const sourceCard = this._swipeCardEl
            const noteAppEl = sourceCard?.closest('.note-app')
            const appStyle = noteAppEl ? getComputedStyle(noteAppEl) : null
            const w = sourceCard ? sourceCard.offsetWidth : 200
            const ghost = sourceCard ? sourceCard.cloneNode(true) : document.createElement('div')

            let cssVars = ''
            if (appStyle) {
                const vars = ['--note-text-primary', '--note-text-secondary', '--note-border', '--note-list-bg', '--note-item-hover']
                cssVars = vars.map(v => v + ':' + appStyle.getPropertyValue(v).trim()).join(';') + ';'
            }
            if (sourceCard) {
                const cs = getComputedStyle(sourceCard)
                ghost.style.background = cs.backgroundColor
            }
            ghost.style.cssText = 'position:fixed;z-index:200010;pointer-events:none;opacity:0.9;' +
                'width:' + w + 'px;will-change:transform;transition:none;border-radius:6px;' +
                'box-shadow:0 4px 16px rgba(0,0,0,0.15);' +
                'background:' + (ghost.style.background || '') + ';' +
                'transform:translate(' + (x - w / 2) + 'px,' + (y - 20) + 'px);' + cssVars
            document.body.appendChild(ghost)
            this._dragGhost = ghost
            this._dragGhostW = w

            document.body.style.userSelect = 'none'
            document.body.style.webkitUserSelect = 'none'
            this._dragHighlightEl = null
        },
        _cleanupTouchDrag() {
            this._touchDragActive = false
            this._touchDragPending = false
            this.touchDragReadyId = null
            if (this._dragHighlightEl) {
                this._dragHighlightEl.classList.remove('drag-over')
                this._dragHighlightEl.classList.remove('drag-over-trash')
                this._dragHighlightEl = null
            }
            if (this._dragGhost) {
                this._dragGhost.remove()
                this._dragGhost = null
            }
            this._touchDragNote = null
            document.body.style.userSelect = ''
            document.body.style.webkitUserSelect = ''
            this._cleanupTouchDragListeners()
        },
        _cleanupTouchDragListeners() {
            document.removeEventListener('touchmove', this._onTouchDragMove)
            document.removeEventListener('touchend', this._onTouchDragEnd)
            document.removeEventListener('touchcancel', this._onTouchDragEnd)
        },

        onDragStart(event, note) {
            if (this._touchDragActive || this._touchDragPending) {
                event.preventDefault()
                return
            }
            if (this._isSwipeGesture) {
                event.preventDefault()
                return
            }
            if (this._swipeStartX != null && note.is_owner !== false && this.selectedNoteIds.length <= 1) {
                const dx = event.clientX - this._swipeStartX
                const dy = event.clientY - this._swipeStartY
                if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= 2) {
                    event.preventDefault()
                    this._swipeLocked = true
                    this._isSwipeGesture = true
                    this._swiped = true
                    this._swipingNote = this._swipingNote || note
                    clearTimeout(this._longPressTimer)
                    this._longPressReady = false
                    this.swipingNoteId = this._swipingNote.id
                    document.addEventListener('mousemove', this._onDocMouseMove)
                    document.addEventListener('mouseup', this._onDocMouseUp)
                    const maxSwipe = 120
                    this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
                    this._checkSwipeThreshold()
                    return
                }
            }
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
            this.swipingNoteId = null
            this.swipeX = 0

            if (note.is_owner === false) {
                event.preventDefault()
                return
            }
            event.dataTransfer.setData('noteId', String(note.id))
            event.dataTransfer.effectAllowed = 'move'

            const isBatchDrag = this.selectedSet.has(note.id) && this.selectedNoteIds.length > 1
            if (isBatchDrag) {
                event.dataTransfer.setData('noteIds', JSON.stringify(this.selectedNoteIds))
            }

            const el = event.currentTarget
            const rect = el.getBoundingClientRect()

            const listEl = el.closest('.note-list')
            const noteAppEl = el.closest('.note-app')
            const listStyle = listEl ? getComputedStyle(listEl) : getComputedStyle(el.parentElement)
            const bgColor = listStyle.backgroundColor || '#ffffff'
            const appStyle = noteAppEl ? getComputedStyle(noteAppEl) : null
            const cssVars = appStyle ? '--note-text-primary:' + appStyle.getPropertyValue('--note-text-primary').trim() + ';--note-text-secondary:' + appStyle.getPropertyValue('--note-text-secondary').trim() + ';--note-border:' + appStyle.getPropertyValue('--note-border').trim() + ';--note-list-bg:' + appStyle.getPropertyValue('--note-list-bg').trim() + ';' : ''

            if (isBatchDrag) {
                const count = this.selectedNoteIds.length
                const layers = Math.min(count, 3)
                const stackOffset = (layers - 1) * 4

                const ghost = document.createElement('div')
                ghost.style.cssText = 'position:fixed;top:-1000px;left:-1000px;width:' + rect.width + 'px;height:' + (rect.height + stackOffset) + 'px;pointer-events:none;' + cssVars

                for (let i = layers - 1; i >= 0; i--) {
                    const card = document.createElement('div')
                    const top = i * 4
                    const opacity = i === 0 ? 0.9 : (0.6 - i * 0.15)
                    card.style.cssText = 'position:absolute;left:0;right:0;top:' + top + 'px;height:' + rect.height + 'px;border-radius:6px;background:' + bgColor + ';box-shadow:0 2px 8px rgba(0,0,0,0.12),0 0 0 1px rgba(0,0,0,0.06);opacity:' + opacity + ';overflow:hidden;'
                    if (i === 0) {
                        const clone = el.cloneNode(true)
                        clone.style.cssText = 'margin:0;border-radius:6px;background:' + bgColor + ';'
                        clone.classList.remove('active', 'multi-selected')
                        card.appendChild(clone)
                    }
                    ghost.appendChild(card)
                }

                const badge = document.createElement('span')
                badge.textContent = count
                const accentColor = getComputedStyle(this.$el).getPropertyValue('--panel-accent').trim() || '#007AFF'
                badge.style.cssText = `position:absolute;top:-6px;right:-6px;display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 6px;background:${accentColor};color:#fff;border-radius:11px;font-size:12px;font-weight:600;line-height:1;font-family:-apple-system,BlinkMacSystemFont,sans-serif;box-shadow:0 2px 6px rgba(0,0,0,0.3);`
                ghost.appendChild(badge)

                document.body.appendChild(ghost)
                event.dataTransfer.setDragImage(ghost, event.clientX - rect.left, event.clientY - rect.top)
                this._dragGhost = ghost
            } else if ('ontouchstart' in window) {
                const ghost = el.cloneNode(true)
                ghost.style.cssText = 'position:fixed;top:-1000px;left:-1000px;width:' + rect.width + 'px;background:' + bgColor + ';opacity:0.9;border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.15);pointer-events:none;' + cssVars
                document.body.appendChild(ghost)
                event.dataTransfer.setDragImage(ghost, event.clientX - rect.left, event.clientY - rect.top)
                this._dragGhost = ghost
            }
        },
        onDragEnd(event) {
            if (this._longPressReady) {
                const dx = Math.abs(event.clientX - this._lpStartX)
                const dy = Math.abs(event.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this._longPressed = true
                    this.$emit('noteContextMenu', {clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {}}, this._lpNote)
                }
            }
            this._longPressReady = false
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            if (this._dragGhost) {
                this._dragGhost.remove()
                this._dragGhost = null
            }
        },
        resetSearch() {
            this.searchQuery = ''
        },
    },
    beforeUnmount() {
        clearTimeout(this.searchTimer)
        clearTimeout(this._longPressTimer)
        clearTimeout(this._touchDragTimer)
        document.removeEventListener('pointermove', this.onDocSwipeMove)
        document.removeEventListener('pointerup', this.onDocSwipeUp)
        document.removeEventListener('mousemove', this._onDocMouseMove)
        document.removeEventListener('mouseup', this._onDocMouseUp)
        this._cleanupTouchDragListeners()
    }
}
</script>

<style scoped>
.note-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--note-list-bg, #ffffff);
}

.note-list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--panel-border, #e5e5ea);
    flex-shrink: 0;
}

.note-list-search {
    flex: 1;
}

.note-search-input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid var(--panel-border, #e5e5ea);
    border-radius: 6px;
    font-size: 12px;
    background: var(--note-sidebar-bg, #f2f2f7);
    color: var(--panel-text-primary, #1c1c1e);
    outline: none;
    transition: border-color 0.2s;
}

.note-search-input:focus {
    border-color: var(--panel-accent);
}

.note-search-input::placeholder {
    color: var(--panel-text-secondary, #8e8e93);
}

.note-list-add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: var(--panel-accent);
    color: #fff;
    border-radius: 6px;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity 0.15s;
}

.note-list-add-btn:hover {
    opacity: 0.85;
}

.note-list-items {
    flex: 1;
    overflow-y: auto;
    padding: 4px 0;
}

.note-group-header {
    padding: 8px 12px 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--note-group-header, #8e8e93);
    letter-spacing: 0.3px;
}

.note-swipe-container {
    position: relative;
    overflow: hidden;
    margin: 1px 6px;
    border-radius: 6px;
}

.note-swipe-action {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    align-items: center;
}

.note-swipe-action--pin,
.note-swipe-action--unpin {
    left: 0;
    width: 50%;
    justify-content: flex-start;
    padding-left: 20px;
    background: #FF9500;
}

.note-swipe-action--unpin {
    background: #8E8E93;
}

.note-swipe-action--delete {
    right: 0;
    width: 50%;
    justify-content: flex-end;
    padding-right: 20px;
    background: #FF3B30;
}

.note-item {
    display: flex;
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 6px;
    margin: 0;
    gap: 0;
    transition: background 0.15s, transform 0.2s ease;
    position: relative;
    z-index: 1;
    background: var(--note-list-bg, #ffffff);
    -webkit-user-select: none;
    user-select: none;
    touch-action: pan-y;
}

.note-item:hover {
    background: var(--note-item-hover, #f5f5f5);
}

.note-item.active {
    box-shadow: inset 0 0 0 999px var(--panel-selected-bg);
}

.note-item.multi-selected {
    box-shadow: inset 0 0 0 999px var(--panel-selected-bg), inset 3px 0 0 var(--panel-accent);
}

.note-item.touch-drag-ready {
    transform: scale(1.03);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    opacity: 0.85;
    z-index: 10;
}

.note-item-color-bar {
    width: 4px;
    border-radius: 2px;
    margin-right: 8px;
    flex-shrink: 0;
    align-self: stretch;
}

.note-item-content {
    flex: 1;
    min-width: 0;
}

.note-item-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
}

.note-item-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--panel-text-primary, #1c1c1e);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
}

.note-item-date {
    font-size: 11px;
    color: var(--panel-text-secondary, #8e8e93);
    white-space: nowrap;
    flex-shrink: 0;
}

.note-item-bottom {
    margin-top: 2px;
}

.note-item-snippet {
    font-size: 12px;
    color: var(--panel-text-secondary, #8e8e93);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
}

.note-item-snippet--empty {
    font-style: italic;
    opacity: 0.6;
}

.note-item-meta {
    display: flex;
    gap: 6px;
    margin-top: 4px;
    flex-wrap: wrap;
    align-items: center;
}

.note-item-category {
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 4px;
    background: var(--panel-border, #e5e5ea);
    color: var(--panel-text-secondary, #8e8e93);
    white-space: nowrap;
}

.note-item-shared {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 4px;
    background: rgba(var(--panel-accent-rgb), 0.1);
    color: var(--panel-accent-text);
    white-space: nowrap;
}

.note-item-draft {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 4px;
    background: rgba(230, 162, 60, 0.15);
    color: #e6a23c;
    white-space: nowrap;
}

.note-item-visibility {
    font-size: 10px;
    color: var(--panel-text-secondary, #8e8e93);
    white-space: nowrap;
}

.note-item-agenda {
    margin-left: auto;
    display: flex;
    align-items: center;
    color: var(--panel-accent-text);
    flex-shrink: 0;
}

.note-list-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
}

.note-list-empty-icon {
    font-size: 40px;
    margin-bottom: 12px;
}

.note-list-empty-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--panel-text-primary, #1c1c1e);
    margin-bottom: 4px;
}

.note-list-empty-desc {
    font-size: 12px;
    color: var(--panel-text-secondary, #8e8e93);
}

.note-list-items::-webkit-scrollbar {
    width: 4px;
}
.note-list-items::-webkit-scrollbar-track {
    background: transparent;
}
.note-list-items::-webkit-scrollbar-thumb {
    background: var(--panel-border, #e5e5ea);
    border-radius: 4px;
}
</style>
